import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import type { RouteComponentProps } from 'react-router';
import {
  Briefcase,
  GraduationCap,
  ChevronRight,
  ChevronLeft,
  Check,
  Send,
  Mail,
  Copy,
  ScanSearch,
  User
} from 'lucide-react';
import type { RecruitIdeaPath } from '../Advisor/ClientV2/partials/RecruitIdeaPath';
import { parseQuizLink } from '../CoverQuiz/coverQuizStorage';
import { submitPublicQuiz } from '../../store/clientQuiz/api';
import {
  isTerminalLinkState,
  LinkMessage,
  RetryBanner,
  usePublicQuizLink,
  useQuizSubmit
} from '../CoverQuiz/publicQuizLink';
import { draftLang, linkHasEnded, quizDraftKey, readQuizDraft, useQuizDraft } from '../CoverQuiz/quizDraft';
import { L, listJoin, QuizLangProvider, QuizLangSwitcher, usePageQuizLang } from '../CoverQuiz/quizLang';
import type { QuizLang, Txt } from '../CoverQuiz/quizLang';
import { DISC_LETTERS, discName, isDiscLetter } from '../../constants/disc';
import { DISC_ARCHETYPES } from './careerDiscArchetypes';
import DiscLetterBadge from '../../components/DiscLetterBadge/DiscLetterBadge';
import CareerQuizPartTwo from './CareerQuizPartTwo';
import { DREAM_OPTIONS, HORIZON_OPTIONS, IDEAL_DAY_OPTIONS, IDENTITY_OPTIONS } from './careerDreamData';
import './CareerQuiz.scss';

// 現職痛點 — keys match RECRUIT_PAIN_POINTS
// A label's short form is the part before its first comma (「加班太多」, "Too much overtime").
const PAIN_OPTIONS = [
  {
    key: 'time',
    icon: '🕒',
    label: L('加班太多，沒有自己時間', '加班太多，没有自己时间', 'Too much overtime, no time for myself')
  },
  { key: 'income', icon: '💰', label: L('付出多，回報少', '付出多，回报少', 'Giving a lot, getting little back') },
  {
    key: 'promotion',
    icon: '📈',
    label: L('升職靠年資，不靠實力', '升职靠年资，不靠实力', 'Promotion by seniority, not ability')
  },
  { key: 'politics', icon: '⚖️', label: L('厭倦辦公室政治', '厌倦办公室政治', 'Tired of office politics') },
  {
    key: 'fulfilment',
    icon: '🌱',
    label: L('工作重複，沒有成就感', '工作重复，没有成就感', 'Repetitive work, no sense of achievement')
  },
  {
    key: 'layoff',
    icon: '⚠️',
    label: L('怕被裁員、被取代', '怕被裁员、被取代', 'Worried about layoffs or being replaced')
  },
  { key: 'retire', icon: '⏳', label: L('退休遙遙無期', '退休遥遥无期', 'Retirement feels out of reach') },
  {
    key: 'capital',
    icon: '🏢',
    label: L('想創業，但沒有本錢', '想创业，但没有本钱', 'Want to start a business, but no capital')
  }
];

// Target income options
const INCOME_OPTIONS = [
  {
    value: 30000,
    label: L('HK$ 30,000 / 月', 'HK$ 30,000 / 月', 'HK$ 30,000 / month'),
    desc: L('穩健過渡 · 生活無憂', '稳健过渡 · 生活无忧', 'A steady transition · Comfortable living')
  },
  {
    value: 50000,
    label: L('HK$ 50,000 / 月', 'HK$ 50,000 / 月', 'HK$ 50,000 / month'),
    desc: L('突破中產 · 財務有彈性', '突破中产 · 财务有弹性', 'Beyond middle class · Financial flexibility')
  },
  {
    value: 80000,
    label: L('HK$ 80,000 / 月', 'HK$ 80,000 / 月', 'HK$ 80,000 / month'),
    desc: L('高薪水平 · 生活升級', '高薪水平 · 生活升级', 'High earner · A better lifestyle')
  },
  {
    value: 120000,
    label: L('HK$ 120,000+ / 月', 'HK$ 120,000+ / 月', 'HK$ 120,000+ / month'),
    desc: L('理想生活 · 更多選擇', '理想生活 · 更多选择', 'Your ideal life · More choices')
  }
];

// 核心優勢 — keys match RECRUIT_STRENGTHS.
const STRENGTH_OPTIONS = [
  { key: 'network', icon: '👥', label: L('人脈廣，朋友多', '人脉广，朋友多', 'Well connected, lots of friends') },
  { key: 'communicator', icon: '🗣️', label: L('會說話，有感染力', '会说话，有感染力', 'Good talker, inspiring') },
  { key: 'driven', icon: '🚀', label: L('有目標，肯拼搏', '有目标，肯拼搏', 'Goal-driven, hard-working') },
  { key: 'disciplined', icon: '🎯', label: L('自律有規劃', '自律有规划', 'Self-disciplined and organised') },
  { key: 'learner', icon: '💡', label: L('學什麼都快', '学什么都快', 'Quick to learn anything') },
  { key: 'warm', icon: '❤️', label: L('善解人意，有同理心', '善解人意，有同理心', 'Understanding, empathetic') },
  { key: 'salesExp', icon: '🤝', label: L('有商業觸覺', '有商业触觉', 'Good business sense') },
  { key: 'resilient', icon: '🛡️', label: L('抗壓力強', '抗压力强', 'Handles pressure well') }
];

// 快問快答 — three gut-feel either/or picks
const RAPID_QUESTIONS = [
  {
    key: 'workday',
    question: L('理想的工作日？', '理想的工作日？', 'Your ideal workday?'),
    options: [
      { key: 'freedom', icon: '🏖️', label: L('時間自己安排', '时间自己安排', 'I set my own schedule') },
      { key: 'goal', icon: '🏆', label: L('目標清晰全力衝', '目标清晰全力冲', 'Clear goals, full speed') }
    ]
  },
  {
    key: 'weekend',
    question: L('週末怎樣充電？', '周末怎样充电？', 'How do you recharge at the weekend?'),
    options: [
      { key: 'social', icon: '🎉', label: L('約朋友聚會', '约朋友聚会', 'Meeting up with friends') },
      { key: 'solo', icon: '🛋️', label: L('安靜獨處休息', '安静独处休息', 'Quiet time on my own') }
    ]
  },
  {
    key: 'team',
    question: L('理想團隊像什麼？', '理想团队像什么？', 'Your ideal team is like…'),
    options: [
      { key: 'rocket', icon: '🚀', label: L('戰隊一起衝', '战队一起冲', 'A squad charging ahead together') },
      { key: 'clan', icon: '🌳', label: L('家人般互相扶持', '家人般互相扶持', 'A family that supports each other') }
    ]
  }
] as const;

// option key → display, for echoing the picks on the report
const RAPID_LOOKUP: Record<string, { icon: string; label: Txt }> = {};
RAPID_QUESTIONS.forEach(q => q.options.forEach(o => (RAPID_LOOKUP[o.key] = { icon: o.icon, label: o.label })));

// Step names — 11 steps total (0 = Welcome, 10 = Report). 想要的支援 moved to
// 第二部分 on 2026-10-01: its options (找客戶, 見客, 考牌) say what the job is, and
// part 1 is meant to give value before anything reads like recruiting.
const STEP_LABELS: Txt[] = [
  L('開始前', '开始前', 'Before you start'),
  L('能量快問', '能量快问', 'Quick energy check'),
  L('核心天賦', '核心天赋', 'Core talents'),
  L('性格原型', '性格原型', 'Personality type'),
  L('職涯起點', '职涯起点', 'Career starting point'),
  L('能量瓶頸', '能量瓶颈', 'Energy drains'),
  L('理想的一天', '理想的一天', 'Your ideal day'),
  L('夢想', '梦想', 'Your dream'),
  L('身價回報', '身价回报', 'Your worth'),
  L('3 年後的你', '3 年后的你', 'You in 3 years'),
  L('專屬報告', '专属报告', 'Your report')
];

/** The part of a label before its first comma — the tag form on the report. */
const shortOf = (label: string, lang: QuizLang) => label.split(lang === 'en' ? ',' : '，')[0];

const COPY = {
  yourAdviser: L('你的顧問', '你的顾问', 'your adviser'),
  adviserNamed: (name: string) => L(`${name}顧問`, `${name}顾问`, `your adviser ${name}`),
  brand: L('PortfoPlus 職涯導航', 'PortfoPlus 职涯导航', 'PortfoPlus Career Compass'),
  brandFor: (name: string) =>
    L(
      `為 ${name} 專屬準備 · 職場天賦微測驗`,
      `为 ${name} 专属准备 · 职场天赋微测验`,
      `Made for ${name} · Work talent quiz`
    ),
  brandGuest: L('職場能量與天賦潛能微測驗', '职场能量与天赋潜能微测验', 'Work energy and talent quiz'),
  pillStart: L('⚡ 2 分鐘微測驗', '⚡ 2 分钟微测验', '⚡ 2-minute quiz'),
  pillDone: L('診斷完成', '诊断完成', 'All done'),
  progress: L('測驗進度', '测验进度', 'Quiz progress'),
  // Time only, never how many questions: a count up front reads as a chore.
  length: L('約 2 分鐘', '约 2 分钟', 'About 2 minutes'),
  title: L('探索你的職場天賦動物', '探索你的职场天赋动物', 'Discover your work-talent animal'),
  subtitle: L(
    '約 2 分鐘，看清個人天賦優勢與身價潛能',
    '约 2 分钟，看清个人天赋优势与身价潜能',
    'In about 2 minutes, see your strengths and earning potential'
  ),
  youWillGet: L('完成後你會得到', '完成后你会得到', "What you'll get"),
  discTypes: L('DISC 四種職場性格', 'DISC 四种职场性格', 'The four DISC work styles'),
  spectrum: L('5 維天賦光譜', '5 维天赋光谱', '5-dimension talent spectrum'),
  spectrumDesc: L('找出你最突出的核心優勢', '找出你最突出的核心优势', 'Find your standout strengths'),
  nameLabel: L('你的稱呼（選填）', '你的称呼（选填）', 'Your name (optional)'),
  namePlaceholder: L('例如：David / 晴天', '例如：David / 晴天', 'e.g. David / Sunny'),
  start: L('開始探索', '开始探索', "Let's go"),
  gutPick: L(
    '憑直覺二選一，不用想太久 ⚡',
    '凭直觉二选一，不用想太久 ⚡',
    "Pick one on instinct, don't overthink it ⚡"
  ),
  back: L('上一步', '上一步', 'Back'),
  next: L('下一題', '下一题', 'Next'),
  strengthsTitle: L('朋友最常誇你什麼？', '朋友最常夸你什么？', 'What do friends praise you for most?'),
  strengthsDesc: (n: number) =>
    L(
      `選 1 ~ 3 項你最認同的特質（已選 ${n}/3）`,
      `选 1 ~ 3 项你最认同的特质（已选 ${n}/3）`,
      `Pick 1–3 that fit you best (${n}/3 picked)`
    ),
  replaceHint: L(' · 再選新項將取代最早選擇', ' · 再选新项将取代最早选择', ' · A new pick replaces your first one'),
  discTitle: L('召喚你的職場天賦動物', '召唤你的职场天赋动物', 'Meet your work-talent animal'),
  discDesc: L(
    '經典雙軸模型 · 系統已結合前題初步推導，可隨時撥動微調。',
    '经典双轴模型 · 系统已结合前题初步推导，可随时拨动微调。',
    'A classic two-axis model · worked out from your answers so far; adjust the switches any time.'
  ),
  discGuess: L(
    '系統依據你的能量快問與天賦初步推算：',
    '系统依据你的能量快问与天赋初步推算：',
    'From your quick answers and talents, our first read is: '
  ),
  discAdjust: L(
    '（可自由撥動開關微調最自在的你）',
    '（可自由拨动开关微调最自在的你）',
    ' (flip the switches until it feels like you)'
  ),
  outgoing: L('主動外向', '主动外向', 'Outgoing'),
  outgoingSub: L('節奏快 · 衝勁十足', '节奏快 · 冲劲十足', 'Fast pace · Full of drive'),
  reflective: L('沉穩深思', '沉稳深思', 'Calm and reflective'),
  reflectiveSub: L('節奏穩 · 細緻觀察', '节奏稳 · 细致观察', 'Steady pace · Observant'),
  logical: L('目標邏輯', '目标逻辑', 'Goals and logic'),
  logicalSub: L('重視事實數據與結果', '重视事实数据与结果', 'Values facts, data and results'),
  feeling: L('情感人際', '情感人际', 'Feelings and people'),
  feelingSub: L('重視團隊氣氛與感受', '重视团队气氛与感受', 'Values team spirit and feelings'),
  confirmType: L('確認性格原型', '确认性格原型', 'Confirm my type'),
  pathTitle: L('你目前的職場起點？', '你目前的职场起点？', 'Where are you starting from?'),
  pathDesc: L('讓我們了解你現在的起點。', '让我们了解你现在的起点。', 'Tell us where you are now.'),
  experienced: L('💼 在職人士', '💼 在职人士', '💼 Working professional'),
  experiencedSub: L('已工作一段時間', '已工作一段时间', 'Been working for a while'),
  graduate: L('🎓 應屆畢業 / 新人', '🎓 应届毕业 / 新人', '🎓 Graduate / newcomer'),
  graduateSub: L(
    '畢業 1–2 年，渴望快速成長與專業導師',
    '毕业 1–2 年，渴望快速成长与专业导师',
    'Graduated 1–2 years ago, eager to grow fast with a mentor'
  ),
  tenure: L('工作年資：', '工作年资：', 'Years of work:'),
  tenureUnder1: L('1 年以下', '1 年以下', 'Under 1 year'),
  tenure1to3: L('1 ~ 3 年', '1 ~ 3 年', '1–3 years'),
  tenure3to6: L('3 ~ 6 年', '3 ~ 6 年', '3–6 years'),
  tenureOver6: L('6 年以上', '6 年以上', 'Over 6 years'),
  painTitle: L('現階段最消耗你能量的是？', '现阶段最消耗你能量的是？', 'What drains your energy most right now?'),
  painDesc: (n: number) =>
    L(
      `找出能避開的職場內耗（選 1 ~ 3 項，已選 ${n}/3）`,
      `找出能避开的职场内耗（选 1 ~ 3 项，已选 ${n}/3）`,
      `Find the drains you could avoid (pick 1–3, ${n}/3 picked)`
    ),
  painProof: L(
    '💡 超過 72% 的優秀職場人在此題選出時間或回報瓶頸',
    '💡 超过 72% 的优秀职场人在此题选出时间或回报瓶颈',
    '💡 Over 72% of high performers pick time or reward here'
  ),
  idealTitle: L('你理想的一天是怎樣的？', '你理想的一天是怎样的？', 'What does your ideal day look like?'),
  idealDesc: (n: number) =>
    L(
      `選 2 個最想要的畫面（已選 ${n}/2）`,
      `选 2 个最想要的画面（已选 ${n}/2）`,
      `Pick the 2 scenes you want most (${n}/2 picked)`
    ),
  dreamTitle: L(
    '如果收入不再是問題，你最想做什麼？',
    '如果收入不再是问题，你最想做什么？',
    "If money weren't an issue, what would you most like to do?"
  ),
  dreamDesc: L('選一個最想實現的', '选一个最想实现的', 'Pick the one you most want to make happen'),
  incomeTitle: L(
    '如果不設限，你認為未來的自己值得怎樣的身價回報？',
    '如果不设限，你认为未来的自己值得怎样的身价回报？',
    'With no limits, what do you think your future self is worth?'
  ),
  incomeDesc: L(
    '敢想敢衝，才有機會突破天花板 🚀',
    '敢想敢冲，才有机会突破天花板 🚀',
    'Dream big and go for it to break through the ceiling 🚀'
  ),
  horizonTitle: L('想幾年內做到？', '想几年内做到？', 'Within how many years?'),
  identityTitle: L(
    '3 年後，你想別人怎樣形容你？',
    '3 年后，你想别人怎样形容你？',
    'In 3 years, how would you like people to describe you?'
  ),
  makeReport: L('生成我的分析報告 ✨', '生成我的分析报告 ✨', 'Create my report ✨'),
  reportOf: (name: string) =>
    name
      ? L(`${name} 的專屬職涯報告`, `${name} 的专属职涯报告`, `${name}'s career report`)
      : L('你的專屬職涯報告', '你的专属职涯报告', 'Your career report'),
  reportFrom: L(
    '根據你的天賦、痛點與性格定位生成',
    '根据你的天赋、痛点与性格定位生成',
    'Based on your talents, pain points and personality'
  ),
  coreAnimal: L('你的職場核心動物', '你的职场核心动物', 'Your core work animal'),
  spectrumHeading: L('🧭 你的 5 維天賦光譜', '🧭 你的 5 维天赋光谱', '🧭 Your 5-dimension talent spectrum'),
  lifeHeading: L('你想要的生活', '你想要的生活', 'The life you want'),
  horizonTag: (years: number, income: string) =>
    L(
      `🎯 ${years} 年內月入 HK$ ${income}`,
      `🎯 ${years} 年内月入 HK$ ${income}`,
      `🎯 HK$ ${income} a month within ${years} years`
    ),
  talentHeading: L('你的天賦標籤', '你的天赋标签', 'Your talent tags'),
  painHeading: L('想擺脫的束縛', '想摆脱的束缚', 'What you want to break free from'),
  rapidHeading: L('你的快問快答', '你的快问快答', 'Your quick picks'),
  insightHeading: L('職涯洞察', '职涯洞察', 'Career insight'),
  insightBefore: L('身為', '身为', 'As a '),
  insightMiddle: L(
    '，你的束縛不是能力，而是平台。在憑實力累積的賽道上，「',
    '，你的束缚不是能力，而是平台。在凭实力累积的赛道上，「',
    ', what holds you back is the platform, not your ability. On a track that rewards ability, "'
  ),
  insightAfter: L('」反而會成為你的燃料。', '」反而会成为你的燃料。', '" becomes your fuel.'),
  insightFallback: L('現職的限制', '现职的限制', 'the limits of your current job'),
  copied: L('已複製報告摘要！', '已复制报告摘要！', 'Report summary copied!'),
  copy: L('複製報告精華', '复制报告精华', 'Copy report highlights'),
  consent: (adviser: string) =>
    L(
      `按「保存並發送給顧問」即表示你同意把結果分享給${adviser}，方便準備合適的資料。`,
      `按「保存并发送给顾问」即表示你同意把结果分享给${adviser}，方便准备合适的资料。`,
      `By tapping "Save and send to adviser" you agree to share your results with ${adviser}, so they can prepare suitable information.`
    ),
  send: L('保存並發送給顧問', '保存并发送给顾问', 'Save and send to adviser'),
  sendHint: L(
    '你的顧問會按你的答案，準備適合你的分享',
    '你的顾问会按你的答案，准备适合你的分享',
    'Your adviser will prepare something that suits you, based on your answers'
  ),
  trialDone: L('✅ 試玩完成', '✅ 试玩完成', '✅ Trial complete'),
  trialNote: L('這是預覽版，答案不會儲存。', '这是预览版，答案不会储存。', 'This is a preview; answers are not saved.'),
  saved: L('✅ 已成功保存並同步！', '✅ 已成功保存并同步！', '✅ Saved and sent!'),
  savedNote: L(
    '顧問已收到你的職涯測驗報告，期待下一次深入交流！',
    '顾问已收到你的职涯测验报告，期待下一次深入交流！',
    'Your adviser has your career report; looking forward to talking it through next time!'
  ),
  redo: L('重新填寫測驗', '重新填写测验', 'Take the quiz again'),
  tryPartTwo: L('試玩第二部分', '试玩第二部分', 'Try part 2'),
  summaryTitle: (name: string) =>
    name
      ? L(
          `【${name}的 PortfoPlus 職場天賦報告】`,
          `【${name}的 PortfoPlus 职场天赋报告】`,
          `[${name}'s PortfoPlus work talent report]`
        )
      : L('【我的 PortfoPlus 職場天賦報告】', '【我的 PortfoPlus 职场天赋报告】', '[My PortfoPlus work talent report]'),
  summaryType: L('核心性格：', '核心性格：', 'Core type: '),
  summaryTraits: L('特質：', '特质：', 'Traits: '),
  summaryTalents: L('核心天賦：', '核心天赋：', 'Core talents: '),
  summaryIncome: (income: string) =>
    L(`期待月薪：HK$ ${income} / 月`, `期待月薪：HK$ ${income} / 月`, `Target income: HK$ ${income} / month`),
  powers: {
    influence: L('影響感召力', '影响感召力', 'Influence'),
    drive: L('目標開拓力', '目标开拓力', 'Drive'),
    logic: L('邏輯專業度', '逻辑专业度', 'Logic and expertise'),
    empathy: L('團隊共情力', '团队共情力', 'Team empathy'),
    growth: L('潛能成長值', '潜能成长值', 'Growth potential')
  },
  tierCore: L('核心天賦', '核心天赋', 'Core talents'),
  tierStrong: L('明顯強項', '明显强项', 'Clear strengths'),
  tierGrow: L('成長空間', '成长空间', 'Room to grow')
};

export type CareerQuizProps = RouteComponentProps;

/** Stagger helper */
const staggerDelay = (idx: number): React.CSSProperties => ({ animationDelay: `${Math.min(idx * 45, 360)}ms` });

/** DISC smart deduction helper based on rapid picks and strengths */
const deduceDisc = (v: Record<string, string>, s: string[]) => {
  let outgoingScore = 0;
  let logicalScore = 0;

  // Vibes
  if (v['workday'] === 'goal') logicalScore += 2;
  if (v['workday'] === 'freedom') outgoingScore += 1;
  if (v['weekend'] === 'social') outgoingScore += 2;
  if (v['weekend'] === 'solo') outgoingScore -= 2;
  if (v['team'] === 'rocket') logicalScore += 2;
  if (v['team'] === 'clan') logicalScore -= 2;

  // Strengths
  if (s.includes('communicator')) {
    outgoingScore += 2;
    logicalScore -= 1;
  }
  if (s.includes('network')) {
    outgoingScore += 2;
    logicalScore -= 1;
  }
  if (s.includes('driven')) {
    outgoingScore += 1;
    logicalScore += 2;
  }
  if (s.includes('salesExp')) {
    outgoingScore += 1;
    logicalScore += 1;
  }
  if (s.includes('disciplined')) {
    outgoingScore -= 1;
    logicalScore += 2;
  }
  if (s.includes('learner')) {
    logicalScore += 1;
  }
  if (s.includes('warm')) {
    logicalScore -= 2;
  }
  if (s.includes('resilient')) {
    logicalScore += 1;
  }

  return {
    outgoing: outgoingScore >= 0,
    logical: logicalScore >= 0
  };
};

/**
 * What coming back to the link brings back (quizDraft useQuizDraft). 第二部分
 * keeps its own answers, in CareerQuizPartTwo, under the same key — `lang` is
 * in both, so either brings the page's language back.
 */
type CareerDraft = {
  step: number;
  lang: string;
  candidateName: string;
  path: RecruitIdeaPath;
  yearsOfExp: number | null;
  painPoints: string[];
  targetIncome: number;
  strengths: string[];
  isOutgoing: boolean;
  isLogical: boolean;
  discManuallyEdited: boolean;
  vibes: Record<string, string>;
  rapidIdx: number;
  idealDay: string[];
  dream: string | null;
  horizon: number | null;
  identity: string | null;
};

export const CareerQuizComponent: React.FC<CareerQuizProps> = ({ location }) => {
  const searchStr = location?.search || (typeof window !== 'undefined' ? window.location.search : '');
  const { token } = parseQuizLink(searchStr);
  // Answers in progress survive a reload (iOS Safari reloads a discarded tab) — see useQuizDraft.
  const draftKey = quizDraftKey('career', token);
  const [draft] = useState(() => readQuizDraft<CareerDraft>(draftKey));

  // Steps: 0 Welcome · 1 Rapid · 2 Strengths · 3 DISC · 4 Path & Job · 5 Pain · 6 Ideal day · 7 Dream
  //        · 8 Income (+ horizon) · 9 Identity · 10 Result (Support is in part 2)
  const [step, setStep] = useState(draft?.step ?? 0);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  const goToStep = (next: number) => {
    setDirection(next >= step ? 'forward' : 'back');
    setStep(next);
  };

  // A `?t=` link starts 'loading' and resolves on the server; a bare link
  // (no `?t=`) is a guest preview and never fetches anything.
  const { link, setLink, quiz, reload } = usePublicQuizLink(token, 'career');
  const [lang, setLang] = usePageQuizLang(quiz?.locale, searchStr, draftLang(draft?.lang));
  const t = (text: Txt) => text[lang];
  const langBar = <QuizLangSwitcher lang={lang} onChange={setLang} testId="career-quiz-lang" />;
  const isGuest = link === 'guest';
  const adviserLabel = t(quiz?.adviserName ? COPY.adviserNamed(quiz.adviserName) : COPY.yourAdviser);

  // Form states — the candidate's display name is the invitation's own `displayName`
  // (empty for a guest preview), still editable on the welcome step.
  const [candidateName, setCandidateName] = useState(draft?.candidateName ?? '');
  useEffect(() => {
    // A name the candidate already edited (restored from the draft) is theirs to keep.
    if (quiz?.displayName && draft?.candidateName === undefined) setCandidateName(quiz.displayName);
  }, [quiz?.displayName, draft]);

  const [path, setPath] = useState<RecruitIdeaPath>(draft?.path ?? 'experienced');
  // `null` until the candidate taps a tenure band — "not answered", not a guessed default.
  const [yearsOfExp, setYearsOfExp] = useState<number | null>(draft?.yearsOfExp ?? null);
  const [painPoints, setPainPoints] = useState<string[]>(draft?.painPoints ?? []);
  const [targetIncome, setTargetIncome] = useState<number>(draft?.targetIncome ?? 50000);
  const [strengths, setStrengths] = useState<string[]>(draft?.strengths ?? []);

  // DISC 2-switches:
  const [isOutgoing, setIsOutgoing] = useState(draft?.isOutgoing ?? true);
  const [isLogical, setIsLogical] = useState(draft?.isLogical ?? false);
  const [discManuallyEdited, setDiscManuallyEdited] = useState(draft?.discManuallyEdited ?? false);

  // 快問快答
  const [vibes, setVibes] = useState<Record<string, string>>(draft?.vibes ?? {});
  const [rapidIdx, setRapidIdx] = useState(draft?.rapidIdx ?? 0);
  const [idealDay, setIdealDay] = useState<string[]>(draft?.idealDay ?? []);
  const [dream, setDream] = useState<string | null>(draft?.dream ?? null);
  const [horizon, setHorizon] = useState<number | null>(draft?.horizon ?? null);
  const [identity, setIdentity] = useState<string | null>(draft?.identity ?? null);

  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  // A submit that failed for a reason other than 410/409: the answers stay in
  // memory and a retry banner re-runs the same handler.
  const { submit, sending, failed } = useQuizSubmit<'finish'>(setLink);

  // Kept until the answers are sent: a career link has nothing after them. A
  // 第二部分 link keeps its draft inside CareerQuizPartTwo (same key, so the
  // clear below covers it too).
  useQuizDraft(
    draftKey,
    {
      step,
      lang,
      candidateName,
      path,
      yearsOfExp,
      painPoints,
      targetIncome,
      strengths,
      isOutgoing,
      isLogical,
      discManuallyEdited,
      vibes,
      rapidIdx,
      idealDay,
      dream,
      horizon,
      identity
    },
    { save: link === 'ready' && quiz?.part !== 2 && !submitted, clear: submitted || linkHasEnded(link) }
  );

  // Derive DISC letter
  const discLetter: 'D' | 'I' | 'S' | 'C' = isOutgoing ? (isLogical ? 'D' : 'I') : isLogical ? 'C' : 'S';
  const currentArchetype = DISC_ARCHETYPES[discLetter];
  const currentRapid = RAPID_QUESTIONS[rapidIdx];

  // 第二部分: a real invitation says so itself (`quiz.part === 2`, below). A
  // preview (no token) opens it with `?part=2` (and `?disc=` for the part-1
  // letter), or from part 1's result; a `?t=` link ignores `?part=`.
  const [partTwoOpen, setPartTwoOpen] = useState(false);
  const query = new URLSearchParams(searchStr || '');
  const partTwoByLink = query.get('part') === '2';
  const discParam = (query.get('disc') || '').toUpperCase();
  if (isGuest && (partTwoOpen || partTwoByLink)) {
    return (
      <QuizLangProvider value={lang}>
        <CareerQuizPartTwo
          langBar={langBar}
          disc={partTwoOpen ? discLetter : isDiscLetter(discParam) ? discParam : 'S'}
          candidateName={candidateName}
          showAdviserView
        />
      </QuizLangProvider>
    );
  }

  if (link === 'loading') {
    return (
      <QuizLangProvider value={lang}>
        <div className="career-quiz-page" data-testid="career-quiz" lang={lang}>
          <div className="career-quiz-container">
            {langBar}
            <div className="cq-card cq-link-message" data-testid="career-quiz-loading">
              {t(L('載入中…', '加载中…', 'Loading…'))}
            </div>
          </div>
        </div>
      </QuizLangProvider>
    );
  }
  if (isTerminalLinkState(link)) {
    return (
      <QuizLangProvider value={lang}>
        <div className="career-quiz-page" data-testid="career-quiz" lang={lang}>
          <div className="career-quiz-container">
            {langBar}
            <LinkMessage state={link} testIdPrefix="career-quiz" className="cq-card cq-link-message" onRetry={reload} />
          </div>
        </div>
      </QuizLangProvider>
    );
  }

  // 第二部分 on its own link: the part-1 letter comes from the server (`disc`),
  // or the candidate picks one. The answers go in as `answers.part2`, which the
  // server merges into the part-1 result (spec §16).
  if (quiz && quiz.part === 2) {
    return (
      <QuizLangProvider value={lang}>
        <CareerQuizPartTwo
          langBar={langBar}
          disc={isDiscLetter(quiz.disc) ? quiz.disc : null}
          candidateName={candidateName}
          draftKey={draftKey}
          submission={{
            adviserLabel,
            sending,
            failed: failed === 'finish',
            submitted,
            onSubmit: part2 =>
              submit(
                'finish',
                () =>
                  submitPublicQuiz(token as string, {
                    answers: { part2 },
                    version: 1,
                    locale: lang
                  }),
                () => setSubmitted(true)
              )
          }}
        />
      </QuizLangProvider>
    );
  }

  // Handlers
  const toggleItem = (list: string[], key: string, max: number, setter: (val: string[]) => void) => {
    if (list.includes(key)) {
      setter(list.filter(k => k !== key));
    } else {
      if (list.length >= max) {
        setter([...list.slice(1), key]);
      } else {
        setter([...list, key]);
      }
    }
  };

  const pickRapid = (qKey: string, optKey: string) => {
    const isNew = !vibes[qKey];
    setVibes(prev => ({ ...prev, [qKey]: optKey }));
    if (isNew && rapidIdx < RAPID_QUESTIONS.length - 1) {
      window.setTimeout(() => setRapidIdx(i => Math.min(i + 1, RAPID_QUESTIONS.length - 1)), 320);
    }
  };

  const handleGoToDisc = () => {
    if (!discManuallyEdited) {
      const deduced = deduceDisc(vibes, strengths);
      setIsOutgoing(deduced.outgoing);
      setIsLogical(deduced.logical);
    }
    goToStep(3);
  };

  // A guest (preview) link has no adviser to share with, so nothing is sent — the
  // report still shows as completed. A real invitation submits to the server; a
  // failure leaves the answers in place with a retry banner (410 → the link has
  // since expired; 409 → already completed).
  const handleFinish = () => {
    if (isGuest) {
      setSubmitted(true);
      return;
    }
    submit(
      'finish',
      () =>
        submitPublicQuiz(token as string, {
          answers: {
            path,
            yearsOfExp: path === 'graduate' ? 0 : yearsOfExp,
            targetIncome,
            painPoints,
            strengths,
            discLetter,
            vibes: Object.values(vibes),
            idealDay,
            dream,
            horizon,
            identity
          },
          version: 1,
          locale: lang
        }),
      () => setSubmitted(true)
    );
  };

  const handleCopyReport = () => {
    const talents = strengths
      .map(s => STRENGTH_OPTIONS.find(o => o.key === s))
      .filter((o): o is (typeof STRENGTH_OPTIONS)[number] => Boolean(o))
      .map(o => shortOf(t(o.label), lang));
    const summaryText =
      `${t(COPY.summaryTitle(candidateName))}\n` +
      `${t(COPY.summaryType)}${t(currentArchetype.name)} (${currentArchetype.letter})\n` +
      `${t(COPY.summaryTraits)}${t(currentArchetype.tagline)}\n` +
      `${t(COPY.summaryTalents)}${listJoin(talents, lang)}\n` +
      t(COPY.summaryIncome(targetIncome.toLocaleString()));

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(summaryText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        })
        .catch(() => {});
    }
  };

  // 5-Dimension Talent Power calculation
  const getPowerStats = () => {
    const baseMap: Record<
      'D' | 'I' | 'S' | 'C',
      { influence: number; drive: number; logic: number; empathy: number; growth: number }
    > = {
      D: { influence: 88, drive: 96, logic: 85, empathy: 72, growth: 95 },
      I: { influence: 98, drive: 86, logic: 75, empathy: 94, growth: 92 },
      S: { influence: 82, drive: 76, logic: 88, empathy: 96, growth: 88 },
      C: { influence: 76, drive: 84, logic: 98, empathy: 78, growth: 91 }
    };
    const stats = { ...baseMap[discLetter] };
    if (strengths.includes('communicator') || strengths.includes('network')) {
      stats.influence = Math.min(stats.influence + 3, 99);
    }
    if (strengths.includes('driven') || strengths.includes('salesExp')) {
      stats.drive = Math.min(stats.drive + 3, 99);
    }
    if (strengths.includes('disciplined') || strengths.includes('learner')) {
      stats.logic = Math.min(stats.logic + 3, 99);
    }
    if (strengths.includes('warm')) {
      stats.empathy = Math.min(stats.empathy + 3, 99);
    }
    if (targetIncome >= 80000) {
      stats.growth = Math.min(stats.growth + 3, 99);
    }
    return [
      { label: t(COPY.powers.influence), val: stats.influence, color: '#f59e0b' },
      { label: t(COPY.powers.drive), val: stats.drive, color: '#ef4444' },
      { label: t(COPY.powers.logic), val: stats.logic, color: '#3b82f6' },
      { label: t(COPY.powers.empathy), val: stats.empathy, color: '#10b981' },
      { label: t(COPY.powers.growth), val: stats.growth, color: '#8b5cf6' }
    ];
  };

  /** Rank the 5 dimensions into 核心天賦 (top 2) / 明顯強項 (next 2) / 成長空間 (last). */
  const getPowerTiers = () => {
    const ranked = [...getPowerStats()].sort((a, b) => b.val - a.val);
    return [
      { key: 'core', icon: '⭐', label: t(COPY.tierCore), items: ranked.slice(0, 2) },
      { key: 'strong', icon: '✨', label: t(COPY.tierStrong), items: ranked.slice(2, 4) },
      { key: 'grow', icon: '🌱', label: t(COPY.tierGrow), items: ranked.slice(4) }
    ];
  };

  const totalSteps = 10;
  const progressPercent = step === 0 ? 5 : Math.round((step / totalSteps) * 100);

  return (
    <QuizLangProvider value={lang}>
      <div className="career-quiz-page" data-testid="career-quiz" lang={lang}>
        <div className="career-quiz-container">
          {langBar}
          {/* SOS Paper style brand header strip */}
          <header className="cq-sos-header">
            <div className="cq-sos-brand">
              <div className="cq-sos-logo">PFP</div>
              <div className="cq-sos-brand-copy">
                <span className="cq-sos-brand-title">{t(COPY.brand)}</span>
                <span className="cq-sos-brand-desc">
                  {t(candidateName ? COPY.brandFor(candidateName) : COPY.brandGuest)}
                </span>
              </div>
            </div>
            <div className="cq-sos-badge-pill">{t(step <= totalSteps ? COPY.pillStart : COPY.pillDone)}</div>
          </header>

          {/* Progress track */}
          <div className="cq-progress">
            <div
              className="cq-progress-track"
              role="progressbar"
              aria-valuenow={step}
              aria-valuemin={0}
              aria-valuemax={totalSteps}
              aria-label={t(COPY.progress)}
            >
              <div className="cq-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="cq-progress-meta">
              <span className="cq-progress-label">{STEP_LABELS[step] ? t(STEP_LABELS[step]) : ''}</span>
              {/* No "3 / 10": how many questions are left is never shown, only the bar. */}
              <span className="cq-progress-count">{step === 0 ? t(COPY.length) : null}</span>
            </div>
          </div>

          {/* Card Stage */}
          <main className="cq-stage">
            <div key={step} className={`cq-step-frame ${direction === 'forward' ? 'is-forward' : 'is-back'}`}>
              {/* STEP 0: Welcome */}
              {step === 0 && (
                <div className="cq-card cq-card-welcome">
                  <h1 className="cq-title">{t(COPY.title)}</h1>
                  <p className="cq-subtitle">{t(COPY.subtitle)}</p>

                  {/* Informational only — a plain list, so it never reads as tappable buttons. */}
                  <div className="cq-welcome-highlights">
                    <span className="cq-highlight-heading">{t(COPY.youWillGet)}</span>
                    <ul>
                      <li>
                        <span className="cq-highlight-icon" aria-hidden="true">
                          🦅
                        </span>
                        <span className="cq-highlight-info">
                          <strong>{t(COPY.discTypes)}</strong>
                          <em>{DISC_LETTERS.map(letter => discName(letter, lang)).join(' · ')}</em>
                        </span>
                      </li>
                      <li>
                        <span className="cq-highlight-icon" aria-hidden="true">
                          🧭
                        </span>
                        <span className="cq-highlight-info">
                          <strong>{t(COPY.spectrum)}</strong>
                          <em>{t(COPY.spectrumDesc)}</em>
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* On an invitation the name is the one the adviser sent it with — it is
                    not sent back with the answers, so it is not editable here. A preview
                    saves nothing, so there the name only personalises the report. */}
                  {isGuest && (
                    <div className="cq-welcome-input-box">
                      <label htmlFor="cq-name">{t(COPY.nameLabel)}</label>
                      <div className="cq-input-wrap">
                        <User size={16} className="cq-input-icon" />
                        <input
                          id="cq-name"
                          type="text"
                          placeholder={t(COPY.namePlaceholder)}
                          value={candidateName}
                          onChange={e => setCandidateName(e.target.value)}
                          className="cq-input"
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="cq-btn-primary cq-btn-large"
                    data-testid="career-quiz-start"
                    onClick={() => goToStep(1)}
                  >
                    {t(COPY.start)} <ChevronRight size={18} />
                  </button>
                </div>
              )}

              {/* STEP 1: 快問快答 (Rapid Vibes) — 3 gut-feel either/or picks */}
              {step === 1 && (
                <div className="cq-card cq-card-rapid">
                  <div className="cq-rapid-dots" aria-hidden="true">
                    {RAPID_QUESTIONS.map((q, i) => (
                      <span
                        key={q.key}
                        className={`cq-rapid-dot ${vibes[q.key] ? 'is-done' : ''} ${
                          i === rapidIdx ? 'is-current' : ''
                        }`}
                      />
                    ))}
                  </div>

                  <div key={rapidIdx} className="cq-rapid-stage">
                    <h2 className="cq-question">{t(currentRapid.question)}</h2>
                    <p className="cq-desc">{t(COPY.gutPick)}</p>

                    <div className="cq-rapid-options">
                      {currentRapid.options.map((opt, idx) => {
                        const selected = vibes[currentRapid.key] === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            className={`cq-rapid-option cq-anim-item ${selected ? 'is-selected' : ''}`}
                            style={staggerDelay(idx)}
                            onClick={() => pickRapid(currentRapid.key, opt.key)}
                          >
                            <span className="cq-rapid-emoji">{opt.icon}</span>
                            <span className="cq-rapid-label">{t(opt.label)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(0)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      className="cq-btn-primary"
                      disabled={Object.keys(vibes).length < RAPID_QUESTIONS.length}
                      onClick={() => goToStep(2)}
                    >
                      {t(COPY.next)} ({Object.keys(vibes).length}/{RAPID_QUESTIONS.length}) <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: 核心優勢 (Strengths) */}
              {step === 2 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.strengthsTitle)}</h2>
                  <p className="cq-desc">
                    {t(COPY.strengthsDesc(strengths.length))}
                    {strengths.length >= 3 && <span className="cq-limit-hint">{t(COPY.replaceHint)}</span>}
                  </p>

                  <div className="cq-options-list">
                    {STRENGTH_OPTIONS.map((opt, idx) => {
                      const selected = strengths.includes(opt.key);
                      return (
                        <div
                          key={opt.key}
                          className={`cq-option-card cq-anim-item ${selected ? 'is-selected' : ''}`}
                          style={staggerDelay(idx)}
                          onClick={() => toggleItem(strengths, opt.key, 3, setStrengths)}
                        >
                          <span className="cq-opt-label">{t(opt.label)}</span>
                          <div className={`cq-checkbox ${selected ? 'is-checked' : ''}`}>
                            {selected && <Check size={14} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(1)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" disabled={strengths.length < 1} onClick={handleGoToDisc}>
                      {t(COPY.next)} ({strengths.length}/3) <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: DISC 性格原型 (Smart Deduction + 2-Switch Adjust) */}
              {step === 3 && (
                <div className="cq-card cq-card-disc">
                  <h2 className="cq-question">{t(COPY.discTitle)}</h2>
                  <p className="cq-desc">{t(COPY.discDesc)}</p>

                  {/* AI Deduction recommendation badge */}
                  <div className="cq-disc-recommend-badge">
                    <ScanSearch size={16} className="cq-sparkle-icon" />
                    <span>
                      {t(COPY.discGuess)}
                      <strong>
                        {t(currentArchetype.name)} ({currentArchetype.letter})
                      </strong>
                      {t(COPY.discAdjust)}
                    </span>
                  </div>

                  {/* 2 Switches */}
                  <div className="cq-switches-box">
                    {/* Switch 1 */}
                    <div className="cq-switch-unit">
                      <div className="cq-switch-labels">
                        <span
                          className={isOutgoing ? 'is-active-side' : ''}
                          onClick={() => {
                            setIsOutgoing(true);
                            setDiscManuallyEdited(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          🔥 <strong>{t(COPY.outgoing)}</strong>
                          <small className="cq-switch-sub">{t(COPY.outgoingSub)}</small>
                        </span>
                        <span
                          className={!isOutgoing ? 'is-active-side' : ''}
                          onClick={() => {
                            setIsOutgoing(false);
                            setDiscManuallyEdited(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          🌿 <strong>{t(COPY.reflective)}</strong>
                          <small className="cq-switch-sub">{t(COPY.reflectiveSub)}</small>
                        </span>
                      </div>
                      <div
                        className="cq-toggle-pill"
                        onClick={() => {
                          setIsOutgoing(!isOutgoing);
                          setDiscManuallyEdited(true);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className={`cq-slider-thumb ${isOutgoing ? 'is-left' : 'is-right'}`}>
                          {isOutgoing ? '🔥' : '🌿'}
                        </div>
                      </div>
                    </div>

                    {/* Switch 2 */}
                    <div className="cq-switch-unit">
                      <div className="cq-switch-labels">
                        <span
                          className={isLogical ? 'is-active-side' : ''}
                          onClick={() => {
                            setIsLogical(true);
                            setDiscManuallyEdited(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          🎯 <strong>{t(COPY.logical)}</strong>
                          <small className="cq-switch-sub">{t(COPY.logicalSub)}</small>
                        </span>
                        <span
                          className={!isLogical ? 'is-active-side' : ''}
                          onClick={() => {
                            setIsLogical(false);
                            setDiscManuallyEdited(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          ❤️ <strong>{t(COPY.feeling)}</strong>
                          <small className="cq-switch-sub">{t(COPY.feelingSub)}</small>
                        </span>
                      </div>
                      <div
                        className="cq-toggle-pill"
                        onClick={() => {
                          setIsLogical(!isLogical);
                          setDiscManuallyEdited(true);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className={`cq-slider-thumb ${isLogical ? 'is-left' : 'is-right'}`}>
                          {isLogical ? '🎯' : '❤️'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Revealed Archetype Card */}
                  <div
                    key={discLetter}
                    className="cq-archetype-preview"
                    style={{ borderColor: currentArchetype.color, backgroundColor: currentArchetype.bg }}
                  >
                    <div className="cq-archetype-top">
                      <span className="cq-archetype-emoji">{currentArchetype.emoji}</span>
                      <div>
                        <h3 style={{ color: currentArchetype.color }}>
                          {t(currentArchetype.name)}
                          <DiscLetterBadge letter={currentArchetype.letter} />
                        </h3>
                        <p className="cq-archetype-tagline">{t(currentArchetype.tagline)}</p>
                      </div>
                    </div>
                    <ul className="cq-archetype-traits">
                      {currentArchetype.traits.map((trait, idx) => (
                        <li key={idx}>{t(trait)}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(2)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" onClick={() => goToStep(4)}>
                      {t(COPY.confirmType)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: 職涯起點 (Path & Job) */}
              {step === 4 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.pathTitle)}</h2>
                  <p className="cq-desc">{t(COPY.pathDesc)}</p>

                  <div className="cq-path-selector">
                    <button
                      type="button"
                      className={`cq-path-btn ${path === 'experienced' ? 'is-active' : ''}`}
                      onClick={() => setPath('experienced')}
                    >
                      <Briefcase size={22} />
                      <div>
                        <strong>{t(COPY.experienced)}</strong>
                        <p>{t(COPY.experiencedSub)}</p>
                      </div>
                      {path === 'experienced' && <Check size={18} className="cq-check-icon" />}
                    </button>

                    <button
                      type="button"
                      className={`cq-path-btn ${path === 'graduate' ? 'is-active' : ''}`}
                      onClick={() => setPath('graduate')}
                    >
                      <GraduationCap size={22} />
                      <div>
                        <strong>{t(COPY.graduate)}</strong>
                        <p>{t(COPY.graduateSub)}</p>
                      </div>
                      {path === 'graduate' && <Check size={18} className="cq-check-icon" />}
                    </button>
                  </div>

                  {path === 'experienced' && (
                    <div className="cq-field-group">
                      <label>{t(COPY.tenure)}</label>
                      <div className="cq-tenure-row">
                        {[
                          { label: t(COPY.tenureUnder1), val: 1 },
                          { label: t(COPY.tenure1to3), val: 2 },
                          { label: t(COPY.tenure3to6), val: 5 },
                          { label: t(COPY.tenureOver6), val: 8 }
                        ].map(item => (
                          <button
                            key={item.val}
                            type="button"
                            className={`cq-chip ${yearsOfExp === item.val ? 'is-selected' : ''}`}
                            onClick={() => setYearsOfExp(item.val)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(3)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" onClick={() => goToStep(5)}>
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 5: 能量瓶頸 (Pain Points) */}
              {step === 5 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.painTitle)}</h2>
                  <p className="cq-desc">
                    {t(COPY.painDesc(painPoints.length))}
                    {painPoints.length >= 3 && <span className="cq-limit-hint">{t(COPY.replaceHint)}</span>}
                  </p>

                  {/* Social Proof Tip */}
                  <div className="cq-social-proof-tip">
                    <span>{t(COPY.painProof)}</span>
                  </div>

                  <div className="cq-options-list">
                    {PAIN_OPTIONS.map((opt, idx) => {
                      const selected = painPoints.includes(opt.key);
                      return (
                        <div
                          key={opt.key}
                          className={`cq-option-card cq-anim-item ${selected ? 'is-selected' : ''}`}
                          style={staggerDelay(idx)}
                          onClick={() => toggleItem(painPoints, opt.key, 3, setPainPoints)}
                        >
                          <span className="cq-opt-label">{t(opt.label)}</span>
                          <div className={`cq-checkbox ${selected ? 'is-checked' : ''}`}>
                            {selected && <Check size={14} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(4)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" disabled={painPoints.length === 0} onClick={() => goToStep(6)}>
                      {t(COPY.next)} ({painPoints.length}/3) <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 6: 理想的一天 (pick two scenes) */}
              {step === 6 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.idealTitle)}</h2>
                  <p className="cq-desc">{t(COPY.idealDesc(idealDay.length))}</p>
                  <div className="cq-options-list">
                    {IDEAL_DAY_OPTIONS.map((opt, idx) => {
                      const selected = idealDay.includes(opt.key);
                      return (
                        <div
                          key={opt.key}
                          data-testid="career-quiz-ideal-day"
                          className={`cq-option-card cq-anim-item ${selected ? 'is-selected' : ''}`}
                          style={staggerDelay(idx)}
                          onClick={() => toggleItem(idealDay, opt.key, 2, setIdealDay)}
                        >
                          <span className="cq-opt-icon">{opt.icon}</span>
                          <span className="cq-opt-label">{t(opt.label)}</span>
                          <div className={`cq-checkbox ${selected ? 'is-checked' : ''}`}>
                            {selected && <Check size={14} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(5)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" disabled={idealDay.length === 0} onClick={() => goToStep(7)}>
                      {t(COPY.next)} ({idealDay.length}/2) <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 7: 夢想 */}
              {step === 7 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.dreamTitle)}</h2>
                  <p className="cq-desc">{t(COPY.dreamDesc)}</p>
                  <div className="cq-options-list">
                    {DREAM_OPTIONS.map((opt, idx) => {
                      const selected = dream === opt.key;
                      return (
                        <div
                          key={opt.key}
                          data-testid="career-quiz-dream"
                          className={`cq-option-card cq-anim-item ${selected ? 'is-selected' : ''}`}
                          style={staggerDelay(idx)}
                          onClick={() => setDream(opt.key)}
                        >
                          <span className="cq-opt-icon">{opt.icon}</span>
                          <span className="cq-opt-label">{t(opt.label)}</span>
                          <div className={`cq-radio ${selected ? 'is-checked' : ''}`} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(6)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" disabled={!dream} onClick={() => goToStep(8)}>
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 8: 身價回報 (Target Income) + 想幾年內做到 */}
              {step === 8 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.incomeTitle)}</h2>
                  <p className="cq-desc">{t(COPY.incomeDesc)}</p>

                  <div className="cq-options-list">
                    {INCOME_OPTIONS.map((opt, idx) => {
                      const selected = targetIncome === opt.value;
                      return (
                        <div
                          key={opt.value}
                          className={`cq-option-card is-prominent cq-anim-item ${selected ? 'is-selected' : ''}`}
                          style={staggerDelay(idx)}
                          onClick={() => setTargetIncome(opt.value)}
                        >
                          <div className="cq-opt-text-col">
                            <strong>{t(opt.label)}</strong>
                            <small>{t(opt.desc)}</small>
                          </div>
                          <div className={`cq-radio ${selected ? 'is-checked' : ''}`} />
                        </div>
                      );
                    })}
                  </div>

                  <p className="cq-subq">{t(COPY.horizonTitle)}</p>
                  <div className="cq-horizon-row">
                    {HORIZON_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid="career-quiz-horizon"
                        className={`cq-horizon ${horizon === opt.value ? 'is-selected' : ''}`}
                        aria-pressed={horizon === opt.value}
                        onClick={() => setHorizon(opt.value)}
                      >
                        {t(opt.label)}
                      </button>
                    ))}
                  </div>

                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(7)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" disabled={horizon === null} onClick={() => goToStep(9)}>
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 9: 3 年後的你 */}
              {step === 9 && (
                <div className="cq-card">
                  <h2 className="cq-question">{t(COPY.identityTitle)}</h2>
                  <div className="cq-options-list">
                    {IDENTITY_OPTIONS.map((opt, idx) => {
                      const selected = identity === opt.key;
                      return (
                        <div
                          key={opt.key}
                          data-testid="career-quiz-identity"
                          className={`cq-option-card cq-anim-item ${selected ? 'is-selected' : ''}`}
                          style={staggerDelay(idx)}
                          onClick={() => setIdentity(opt.key)}
                        >
                          <span className="cq-opt-icon">{opt.icon}</span>
                          <span className="cq-opt-label">{t(opt.label)}</span>
                          <div className={`cq-radio ${selected ? 'is-checked' : ''}`} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="cq-actions">
                    <button className="cq-btn-secondary" onClick={() => goToStep(8)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button className="cq-btn-primary" disabled={!identity} onClick={() => goToStep(10)}>
                      {t(COPY.makeReport)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 10: 專屬報告 (Result Card) */}
              {step === 10 && (
                <div className="cq-card cq-card-result" data-testid="career-quiz-result">
                  <div className="cq-result-header">
                    <h2>{t(COPY.reportOf(candidateName))}</h2>
                    <p>{t(COPY.reportFrom)}</p>
                  </div>

                  {/* Main Report Card */}
                  <div className="cq-report-card">
                    <div
                      className="cq-report-archetype-banner cq-anim-item"
                      style={{ borderLeftColor: currentArchetype.color, ...staggerDelay(1) }}
                    >
                      <div className="cq-report-emoji">{currentArchetype.emoji}</div>
                      <div className="cq-report-info">
                        <span className="cq-report-label">{t(COPY.coreAnimal)}</span>
                        <h3 style={{ color: currentArchetype.color }}>
                          {t(currentArchetype.name)}
                          <DiscLetterBadge letter={currentArchetype.letter} />
                        </h3>
                        <p>{t(currentArchetype.tagline)}</p>
                      </div>
                    </div>

                    {/* 💌 Per-archetype warm letter */}
                    <div className="cq-report-letter cq-anim-item" style={staggerDelay(2)}>
                      <Mail size={15} aria-hidden="true" />
                      <p>{t(currentArchetype.letterLine)}</p>
                    </div>

                    {/* 5-Dimension Talent Power Meter */}
                    <div className="cq-power-section cq-anim-item" style={staggerDelay(3)}>
                      <h4>{t(COPY.spectrumHeading)}</h4>
                      {/* Ranked tiers instead of numeric scores — relative strengths, not a grade. */}
                      <div className="cq-power-tiers">
                        {getPowerTiers().map(tier => (
                          <div key={tier.key} className={`cq-power-tier is-${tier.key}`}>
                            <span className="cq-power-tier-label">
                              {tier.icon} {tier.label}
                            </span>
                            <div className="cq-power-chips">
                              {tier.items.map(p => (
                                <span
                                  key={p.label}
                                  className="cq-power-chip"
                                  style={{ color: p.color, borderColor: p.color, backgroundColor: `${p.color}14` }}
                                >
                                  {p.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Core Strengths */}
                    {/* 你想要的生活 — the dream answers, echoed back as they were picked. */}
                    <div
                      className="cq-report-section cq-anim-item"
                      style={staggerDelay(3)}
                      data-testid="career-quiz-dream-echo"
                    >
                      <h4>{t(COPY.lifeHeading)}</h4>
                      <div className="cq-tags-row">
                        {[
                          ...idealDay.map(key => IDEAL_DAY_OPTIONS.find(o => o.key === key)),
                          DREAM_OPTIONS.find(o => o.key === dream),
                          IDENTITY_OPTIONS.find(o => o.key === identity)
                        ]
                          .filter((opt): opt is { key: string; icon: string; label: Txt } => Boolean(opt))
                          .map(opt => (
                            <span key={opt.key} className="cq-tag is-vibe">
                              {opt.icon} {t(opt.label)}
                            </span>
                          ))}
                        {horizon !== null && (
                          <span className="cq-tag is-vibe">
                            {t(COPY.horizonTag(horizon, targetIncome.toLocaleString()))}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="cq-report-section cq-anim-item" style={staggerDelay(4)}>
                      <h4>{t(COPY.talentHeading)}</h4>
                      <div className="cq-tags-row">
                        {strengths.map((stKey, idx) => {
                          const opt = STRENGTH_OPTIONS.find(o => o.key === stKey);
                          return (
                            <span key={stKey} className="cq-tag is-strength cq-anim-item" style={staggerDelay(idx + 5)}>
                              {opt ? shortOf(t(opt.label), lang) : ''}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Energy Blockers */}
                    <div className="cq-report-section cq-anim-item" style={staggerDelay(6)}>
                      <h4>{t(COPY.painHeading)}</h4>
                      <div className="cq-tags-row">
                        {painPoints.map((pKey, idx) => {
                          const opt = PAIN_OPTIONS.find(o => o.key === pKey);
                          return (
                            <span key={pKey} className="cq-tag is-pain cq-anim-item" style={staggerDelay(idx + 7)}>
                              {opt?.icon} {opt ? shortOf(t(opt.label), lang) : ''}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* 快問快答 echo */}
                    {Object.keys(vibes).length > 0 && (
                      <div className="cq-report-section cq-anim-item" style={staggerDelay(8)}>
                        <h4>{t(COPY.rapidHeading)}</h4>
                        <div className="cq-tags-row">
                          {Object.values(vibes).map((vibeKey, idx) => {
                            const opt = RAPID_LOOKUP[vibeKey];
                            return (
                              <span key={vibeKey} className="cq-tag is-vibe cq-anim-item" style={staggerDelay(idx + 9)}>
                                {opt?.icon} {opt ? t(opt.label) : ''}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Breakthrough Insight */}
                    <div className="cq-report-insight cq-anim-item" style={staggerDelay(10)}>
                      <h4>{t(COPY.insightHeading)}</h4>
                      <p>
                        {t(COPY.insightBefore)}
                        <strong>{t(currentArchetype.name)}</strong>
                        {t(COPY.insightMiddle)}
                        {shortOf(
                          t(PAIN_OPTIONS.find(o => o.key === painPoints[0])?.label || COPY.insightFallback),
                          lang
                        )}
                        {t(COPY.insightAfter)}
                      </p>
                    </div>
                  </div>

                  {/* Actions & Submit */}
                  <div className="cq-result-actions">
                    <button type="button" className="cq-btn-secondary cq-copy-btn" onClick={handleCopyReport}>
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      {t(copied ? COPY.copied : COPY.copy)}
                    </button>

                    {!submitted ? (
                      <div className="cq-submit-zone">
                        {!isGuest && (
                          <p className="cq-consent-note" data-testid="career-quiz-consent-note">
                            {t(COPY.consent(adviserLabel))}
                          </p>
                        )}
                        <button
                          type="button"
                          className="cq-btn-primary cq-btn-large"
                          data-testid="career-quiz-finish"
                          disabled={sending}
                          onClick={handleFinish}
                        >
                          <Send size={18} /> {t(COPY.send)}
                        </button>
                        {!isGuest && <p className="cq-submit-hint">{t(COPY.sendHint)}</p>}
                        {failed === 'finish' && (
                          <RetryBanner
                            onRetry={handleFinish}
                            disabled={sending}
                            testIdPrefix="career-quiz"
                            className="cq-retry-banner"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="cq-submitted-zone">
                        <div className="cq-success-banner">
                          <Check size={20} />
                          <div>
                            {isGuest ? (
                              <>
                                <strong>{t(COPY.trialDone)}</strong>
                                <p>{t(COPY.trialNote)}</p>
                              </>
                            ) : (
                              <>
                                <strong>{t(COPY.saved)}</strong>
                                <p>{t(COPY.savedNote)}</p>
                              </>
                            )}
                          </div>
                        </div>
                        <button className="cq-btn-secondary" onClick={() => goToStep(0)}>
                          {t(COPY.redo)}
                        </button>
                        {isGuest && (
                          <button
                            className="cq-btn-primary"
                            data-testid="career-quiz-try-part2"
                            onClick={() => setPartTwoOpen(true)}
                          >
                            {t(COPY.tryPartTwo)} <ChevronRight size={18} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </QuizLangProvider>
  );
};

export const CareerQuiz = withRouter(CareerQuizComponent);
export default CareerQuiz;
